import { useEffect } from "react";
import styled from "styled-components";
import { Flex, Box } from "rebass/styled-components";
import { LazyQueryExecFunction, ApolloError } from "@apollo/client";

import { GameSelector } from "MVVM/Components/GameSelector";
import { ESupportedGames } from "../../@types";
import { CustomMultiSelect } from "./components";
import { IOption } from "MVVM/Views/Filters/components/index";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";

type TFilterRowContainerProps = {
  filterGame: (game: ESupportedGames) => void;
  game: ESupportedGames;
  handleFilterChange: (option: IOption, filterKey: string) => void;
  fetchFilters: LazyQueryExecFunction<any, any>;
  filterData: {
    filters: { description: string; key: string; options: IOption[] }[];
  };
  filterLoading: boolean;
  filterError: ApolloError | null | undefined;
  handleResetToAllGames: () => void;
  selectedFilters: Record<string, string[]>;
};

const filterTypesByGame: Record<ESupportedGames, string> = {
  [ESupportedGames.CS2]: "CS2",
  [ESupportedGames.CSGO]: "CS_GO",
  [ESupportedGames.DOTA2]: "DOTA2",
  [ESupportedGames.FORTNITE]: "FORTNITE",
  [ESupportedGames.LOL]: "LOL",
  [ESupportedGames.OW2]: "OW2",
  [ESupportedGames.ALL]: "",
  [ESupportedGames.NONE]: "",
};

export const FilterRowContainer = ({
  filterGame,
  game,
  handleFilterChange,
  fetchFilters,
  filterData,
  filterLoading,
  filterError,
  handleResetToAllGames,
  selectedFilters,
}: TFilterRowContainerProps) => {
  useEffect(() => {
    if (game !== ESupportedGames.ALL) {
      fetchFilters({
        variables: { game: filterTypesByGame[game] },
      });
    }
  }, [game, fetchFilters]);

  const renderFilters = () => {
    if (game === ESupportedGames.ALL) {
      return null;
    } else {
      return filterData?.filters?.map(
        ({
          key,
          description,
          options,
        }: {
          key: string;
          description: string;
          options: IOption[];
        }) => (
          <CustomMultiSelect
            key={key}
            options={options}
            placeholder={description}
            isSearchable={options ? options?.length > 6 : false}
            onChange={(option) => handleFilterChange(option, key)}
            value={options.filter(
              (opt) => selectedFilters[key]?.includes(opt.value),
            )}
          />
        ),
      );
    }
  };

  if (filterLoading) {
    return (
      <Flex
        alignItems="center"
        width="100%"
        style={{ gap: "8px", position: "relative" }}
      >
        <GameSelector
          filterGame={filterGame}
          game={game}
          dropdown={true}
          handleResetToAllGames={handleResetToAllGames}
        />
        <LoadingSkeleton
          width={150}
          height={44}
          style={{ borderRadius: "20px" }}
        />
        <LoadingSkeleton
          width={150}
          height={44}
          style={{ borderRadius: "20px" }}
        />
        <LoadingSkeleton
          width={150}
          height={44}
          style={{ borderRadius: "20px" }}
        />
        <LoadingSkeleton
          width={150}
          height={44}
          style={{ borderRadius: "20px" }}
        />
      </Flex>
    );
  }

  if (filterError) {
    return (
      <Flex
        alignItems="center"
        width="100%"
        style={{ gap: "8px", position: "relative" }}
      >
        <GameSelector
          filterGame={filterGame}
          game={game}
          dropdown={true}
          handleResetToAllGames={handleResetToAllGames}
        />
        <ErrorText>Error fetching filters.</ErrorText>
      </Flex>
    );
  }

  return (
    <StyledContainer>
      <GameSelector
        filterGame={filterGame}
        game={game}
        dropdown={true}
        handleResetToAllGames={handleResetToAllGames}
      />
      <Flex
        style={{ gap: "8px", flexWrap: "wrap", justifyContent: "flex-start" }}
      >
        {renderFilters()}
      </Flex>
      {Object.keys(selectedFilters).length > 0 && (
        <ClearButtonContainer>
          <ResetFiltersBox
            onClick={handleResetToAllGames}
            style={{ flexShrink: 0 }}
          >
            Clear
          </ResetFiltersBox>
        </ClearButtonContainer>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled(Flex)`
  align-items: top;
  width: 100%;
  gap: 8px;
  position: relative;
  flex-direction: row;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    flex-direction: column;
  }
`;

const ClearButtonContainer = styled(Flex)`
  margin-left: auto;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    margin-left: initial;
  }
`;

const ResetFiltersBox = styled(Box)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.navy};
  border: 2px solid transparent;
  border-radius: 100px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  min-height: 38px;
  min-width: 100%;
  outline: 0 !important;
  box-sizing: border-box;
  gap: 8px;
  border: 2px solid transparent;
  background-color: ${({ theme }) => theme.colors.navy};
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice};
  padding: 12px 16px;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  position: relative;
  height: 46px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;

  &:hover {
    border: 2px solid ${({ theme }) => theme.colors.darkerEnvy};
    background-color: ${({ theme }) => theme.colors.envyAlpha05};
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
`;
